import PlusIcon from '@cp/src/assets/icons/PlusIcon';
import { Popper, PopperContent, PopperTrigger } from '@cp/src/components/Popper/Popper';
import Button from '@navi/web-ui/lib/primitives/Button/Button';
import Typography from '@navi/web-ui/lib/primitives/Typography/Typography';
import { useDispatch, useSelector } from 'react-redux';
import { postAlternateNumber } from '../../../actions/moreDetailsActions';
import { useParams } from 'react-router-dom';
import { CallIcon, CheckIcon } from '@navi/web-ui/lib/icons';
import { IAddNumberPopperProps } from '../type';
import cx from 'classnames';
import CancelCircleFilled from '@cp/src/assets/icons/CancelCircledFilled';
import { setAddedPhoneNumber } from '../../../feedbackForm/reducer/feedbackSlice';
import { getTelephonesV2 } from '../../../actions/casesDetailsActions';
import { RootState } from '@cp/src/store';
import { CALL_BRIDGE_STATUSES } from '../../../feedbackForm/interfaces/index.type';
import { addClickstreamEvent } from '@cp/src/service/clickStreamEventService';
import { CLICKSTREAM_EVENT_NAMES } from '@cp/src/service/clickStream.constant';
import { getPhoneNumberStatus } from '../actions';
import SuspenseLoader from '@cp/src/components/SkeletonLoader/SuspenseLoader';
import LineLoader from '@cp/src/components/SkeletonLoader/LineLoader';
import { useEffect, useState } from 'react';
import {
  ADD_NUMBER_AND_INITIATE_CALL_SUCCESS_TOAST_MESSAGE,
  BANK_STATEMENT_SOURCE,
  NUMBERS_STATUS
} from '../constants';

const AddNumberPopper = (props: IAddNumberPopperProps) => {
  const { data, children } = props;
  const dispatch = useDispatch();
  const {
    loanId = '',
    customerId = '',
    caseBusinessVertical = '',
    caseReferenceId = ''
  } = useParams();

  const [numberStatusData, setNumberStatusData] = useState<boolean>(false);
  const [statusFailed, setStatusFailed] = useState<boolean>(false);
  const [isExisting, setIsExisting] = useState<boolean>(false);
  const [numberInvalid, setNumberInvalid] = useState<boolean>(false);
  const slashCallStatus = useSelector((state: RootState) => state.common.slashCallStatus);
  const isCiuAgent = useSelector((state: RootState) => state.common.isCiuAgent);

  useEffect(() => {
    addClickstreamEvent(CLICKSTREAM_EVENT_NAMES.LH_DOCUMENTS_BANK_STATEMENT_HYPERLINKING_POPPER);
  }, []);

  const { start, end, narration, isOnModal, setIsExpandedView } = data;
  const identifier = data.filterStart[0].identifier;
  const phoneNumber = narration?.slice(start, end);

  const handleFetchStatus = () => {
    setNumberStatusData(true);
    setStatusFailed(false);
    getPhoneNumberStatus({
      lan: loanId,
      customerId: customerId,
      phoneNumbers: [identifier],
      caseReferenceId,
      caseBusinessVertical
    })
      .then(response => {
        if (response) {
          const status = response[0].validity;
          setIsExisting(status === NUMBERS_STATUS.EXISTS_AND_VALID);
          setNumberInvalid(status === NUMBERS_STATUS.EXISTS_AND_INVALID);
        }
      })
      .catch(error => {
        setStatusFailed(true);
      })
      .finally(() => {
        setNumberStatusData(false);
      });
  };

  const handleNumberAdd = () => {
    addClickstreamEvent(CLICKSTREAM_EVENT_NAMES.LH_DOCUMENTS_BANK_STATEMENT_ADD_PHONE_NUMBER);

    dispatch(
      postAlternateNumber({
        loanaccountnumber: loanId,
        customerreferenceid: customerId,
        caseReferenceId,
        caseBusinessVertical,
        alternateNumber: phoneNumber,
        source: BANK_STATEMENT_SOURCE,
        identifier: identifier,
        successCallback: () => {
          dispatch(getTelephonesV2(loanId, customerId, caseReferenceId, caseBusinessVertical));
        }
      })
    );
  };

  const handleNumberAddAndCall = () => {
    if (isExisting) {
      addClickstreamEvent(CLICKSTREAM_EVENT_NAMES.LH_DOCUMENTS_BANK_STATEMENT_INITIATE_CALL);

      dispatch(setAddedPhoneNumber({ phoneNumber, identifier }));
    } else {
      addClickstreamEvent(
        CLICKSTREAM_EVENT_NAMES.LH_DOCUMENTS_BANK_STATEMENT_ADD_NUMBER_AND_INITIATE_CALL
      );
      dispatch(
        postAlternateNumber({
          loanaccountnumber: loanId,
          customerreferenceid: customerId,
          caseReferenceId,
          caseBusinessVertical,
          alternateNumber: phoneNumber,
          source: BANK_STATEMENT_SOURCE,
          identifier: identifier,
          successToastMessage: ADD_NUMBER_AND_INITIATE_CALL_SUCCESS_TOAST_MESSAGE,
          successCallback: async () => {
            await dispatch(
              getTelephonesV2(loanId, customerId, caseReferenceId, caseBusinessVertical)
            );
            dispatch(setAddedPhoneNumber({ phoneNumber, identifier }));
          }
        })
      );
    }
    if (isOnModal) {
      setIsExpandedView(false);
    }
  };

  return isCiuAgent ? (
    <>
      <span>{children}</span>
    </>
  ) : (
    <span onClick={handleFetchStatus}>
      <Popper placement="top" hideStrategy="referenceHidden">
        <PopperTrigger>
          <span className="underline underline-offset-2 text-grayscale-1 font-medium hover:decoration-blue-base hover:text-blue-base">
            {children}
          </span>
        </PopperTrigger>
        <PopperContent
          noHeader={true}
          arrowColor="var(--bg-primary)"
          className={cx(
            'flex flex-row bg-bg-primary  rounded drop-shadow-md items-center z-[1000001]'
          )}
        >
          {({ closePopper }) => (
            <SuspenseLoader
              loading={numberStatusData}
              fallBack={
                <div className="h-[95px] w-[95px]">
                  <LineLoader childrenType="H1" lineWidth={100} />
                  <LineLoader childrenType="H1" lineWidth={100} />
                  <LineLoader childrenType="H1" lineWidth={10} />
                </div>
              }
            >
              <div className="flex flex-col justify-start" onClick={closePopper}>
                {statusFailed ? (
                  <div className="flex flex-row items-center p-2">
                    <Typography variant="p3" color="var(--grayscale-2)" className="cursor-default">
                      Something went wrong, try again.
                    </Typography>
                  </div>
                ) : (
                  <>
                    {numberInvalid ? (
                      <div className="flex flex-row items-center p-2 gap-[3px]">
                        <CancelCircleFilled width={20} height={20} fillColor="var(--red-base)" />
                        <Typography
                          variant="p3"
                          color="var(--grayscale-2)"
                          className="cursor-default"
                        >
                          Number marked invalid. Cannot be added
                        </Typography>
                      </div>
                    ) : (
                      <>
                        <Button
                          variant="text"
                          startAdornment={
                            isExisting ? (
                              <CheckIcon width={20} height={20} color="var(--green-base)" />
                            ) : (
                              <PlusIcon width={20} height={20} fillColor="var(--blue-base)" />
                            )
                          }
                          className={cx(
                            { '!bg-[#E5F8EB] !rounded-t-[4px] !rounded-b-none': isExisting },
                            'hover:bg-[#F5F9FF] p-4'
                          )}
                          onClick={!isExisting ? handleNumberAdd : undefined}
                        >
                          {isExisting ? (
                            <Typography
                              variant="p3"
                              color="var(--grayscale-1)"
                              className="text-[var(--grayscale-1] cursor-auto"
                            >
                              Phone number already added
                            </Typography>
                          ) : (
                            <Typography variant="p3" color="var(--navi-color-gray-c2)">
                              Add number to contacts
                            </Typography>
                          )}
                        </Button>
                        <Button
                          variant="text"
                          color="var(--blue-base)"
                          startAdornment={
                            <CallIcon width={14} height={14} color="var(--blue-base)" />
                          }
                          className={cx(
                            'hover:bg-[#F5F9FF] hover:rounded-b hover:rounded-t-none flex justify-start gap-2 p-4 ',
                            { '!pl-6': !isExisting }
                          )}
                          onClick={handleNumberAddAndCall}
                          disabled={
                            slashCallStatus === CALL_BRIDGE_STATUSES.IN_PROGRESS ||
                            slashCallStatus === CALL_BRIDGE_STATUSES.INITIATED
                          }
                        >
                          {isExisting ? (
                            <Typography
                              variant="p3"
                              color="var(--grayscale-1)"
                              className="text-[var(--grayscale-1]"
                            >
                              Initiate call
                            </Typography>
                          ) : (
                            <Typography variant="p3" color="var(--navi-color-gray-c2)">
                              Add number & initiate call
                            </Typography>
                          )}
                        </Button>
                      </>
                    )}
                  </>
                )}
              </div>
            </SuspenseLoader>
          )}
        </PopperContent>
      </Popper>
    </span>
  );
};

export default AddNumberPopper;
